import axios from 'axios';
import { toApiError } from './api-error';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1/admin';
export const ACCESS_TOKEN_KEY = 'padm_access_token';
export const REFRESH_TOKEN_KEY = 'padm_refresh_token';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Shared refresh promise — tất cả 401 đồng thời dùng chung 1 refresh call
// Tránh gọi BE nhiều lần khi nhiều request cùng hết token.
let _refreshPromise: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) throw new Error('NO_REFRESH_TOKEN');

  // Bare axios — tránh recursive interceptor nếu /auth/refresh tự trả 401
  const res = await axios.post(
    `${API_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );
  const payload = res.data?.data ?? res.data ?? {};
  const accessToken: string | undefined = payload.access_token;
  const newRefreshToken: string | undefined = payload.refresh_token;

  if (!accessToken) throw new Error('REFRESH_MISSING_TOKEN');

  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (newRefreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

  return accessToken;
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        if (!_refreshPromise) {
          _refreshPromise = doRefresh().finally(() => {
            _refreshPromise = null;
          });
        }
        const accessToken = await _refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.location.href = '/login';
        return Promise.reject(toApiError(error));
      }
    }

    // Convert AxiosError → ApiError với message tiếng Việt từ BE envelope.
    // FE dùng `error.message` sẽ tự động hiện message từ BE.
    return Promise.reject(toApiError(error));
  },
);

// Bare instance for public endpoints (login, refresh) — no token injection
export const noAuthApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

export default api;

// Named re-export for backwards compatibility with existing service files
export { api as apiClient };

// Re-export ApiError + parser cho consumer
export { ApiError, toApiError } from './api-error';

